import React, { useState, useCallback, useEffect } from 'react';
import { FileText, BarChart2, ArrowRight, AlertTriangle, Plus, ExternalLink, Loader2 } from 'lucide-react';

const BackEndUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8080';

const OverviewTab = ({ 
  documentData = {}, 
  analysisResults = {}, 
  makeAuthenticatedRequest 
}) => {
  // États
  const [showSimilar, setShowSimilar] = useState(false);
  const [showVectorAnalysis, setShowVectorAnalysis] = useState(false);
  const [similarDocuments, setSimilarDocuments] = useState([]);
  const [vectorAnalysis, setVectorAnalysis] = useState(null);
  const [loading, setLoading] = useState({
    similar: false,
    vectorAnalysis: false
  });
  const [error, setError] = useState(null);

  // Récupération des documents similaires
  const fetchSimilarDocuments = useCallback(async () => {
    if (!documentData?.id && !documentData?.documentId) return;
    
    const docId = documentData?.id || documentData?.documentId;
    
    setLoading(prev => ({ ...prev, similar: true }));
    setError(null);

    try {
      const response = await makeAuthenticatedRequest(
        `${BackEndUrl}/api/searchDocuments`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            query: documentData.metadata?.title || documentData.fileName,
            options: {
              limit: 5,
              threshold: 0.6
            }
          })
        }
      );
      
      const data = await response.json();
      
      if (data.documents) {
        setSimilarDocuments(
          data.documents
            .filter(doc => (doc.id || doc.documentId) !== docId)
            .slice(0, 5)  // Limiter à 5 documents
        );
      } else {
        setSimilarDocuments([]);
      }
    } catch (error) {
      console.error('Erreur documents similaires:', error);
      setError('Impossible de récupérer les documents similaires');
    } finally {
      setLoading(prev => ({ ...prev, similar: false }));
    }
  }, [documentData, makeAuthenticatedRequest]);

  // Récupération de l'analyse vectorielle
  const fetchVectorAnalysis = useCallback(async () => {
    if (!documentData?.id && !documentData?.documentId) return;
    
    const docId = documentData?.id || documentData?.documentId;
    
    setLoading(prev => ({ ...prev, vectorAnalysis: true }));
    setError(null);

    try {
      const response = await makeAuthenticatedRequest(
        `${BackEndUrl}/api/getVectorAnalysis/${docId}`
      );
      
      const data = await response.json();
      setVectorAnalysis(data.vectorAnalysis);
    } catch (error) {
      console.error('Erreur analyse vectorielle:', error);
      setError('Impossible de récupérer l\'analyse vectorielle');
    } finally {
      setLoading(prev => ({ ...prev, vectorAnalysis: false }));
    }
  }, [documentData, makeAuthenticatedRequest]);

  // Charger les documents similaires quand la section est ouverte
  useEffect(() => {
    if (showSimilar && similarDocuments.length === 0) {
      fetchSimilarDocuments();
    }
  }, [showSimilar, fetchSimilarDocuments, similarDocuments.length]);

  // Charger l'analyse vectorielle quand la section est ouverte
  useEffect(() => {
    if (showVectorAnalysis && !vectorAnalysis) {
      fetchVectorAnalysis();
    }
  }, [showVectorAnalysis, fetchVectorAnalysis, vectorAnalysis]);

  // Utilitaires de style et de présentation
  const getRelevanceColor = (score) => {
    if (score >= 0.75) return 'bg-green-100 text-green-700';
    if (score >= 0.5) return 'bg-blue-100 text-blue-700';
    return 'bg-gray-100 text-gray-700';
  };

  // Métadonnées du document
  const documentTitle = documentData.metadata?.title || documentData.fileName || 'Document sans titre';
  const documentType = documentData.metadata?.documentType || 'Document';
  const summary = analysisResults?.summary || documentData.analysis?.summary || "Aucune synthèse disponible";

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex items-start gap-4 mb-6">
        <div className="p-3 bg-blue-50 text-blue-600 rounded-lg">
          <FileText className="h-6 w-6" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-medium text-gray-900">{documentTitle}</h3>
          <p className="text-sm text-gray-500">{documentType}</p>
        </div>
      </div>

      {/* Synthèse */}
      <div>
        <h4 className="text-base font-medium text-gray-800 mb-3">Synthèse</h4>
        <div className="bg-gray-50 p-4 rounded-lg border border-gray-100">
          <p className="text-gray-700">{summary}</p>
        </div>
      </div>

      {/* Section Analyse Vectorielle */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <BarChart2 className="h-5 w-5 text-blue-500" />
            <h4 className="text-base font-medium text-gray-800">Analyse Vectorielle</h4>
          </div>
          <button 
            onClick={() => setShowVectorAnalysis(!showVectorAnalysis)}
            className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
          >
            {showVectorAnalysis ? 'Masquer les détails' : 'Afficher les détails'}
            <ArrowRight className={`h-4 w-4 transform transition-transform ${showVectorAnalysis ? 'rotate-90' : ''}`} />
          </button>
        </div>

        {showVectorAnalysis && (
          <div className="bg-gray-50 rounded-lg border border-gray-100 p-4">
            {loading.vectorAnalysis ? (
              <div className="flex justify-center py-6">
                <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
              </div>
            ) : error ? (
              <div className="bg-red-50 border border-red-200 p-3 rounded-lg flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-500" />
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            ) : vectorAnalysis ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white p-3 rounded-lg border border-gray-200">
                  <p className="text-xs text-gray-500 mb-1">Nombre de vecteurs</p>
                  <p className="text-lg font-semibold text-gray-900">{vectorAnalysis.vectorCount}</p>
                </div>
                <div className="bg-white p-3 rounded-lg border border-gray-200">
                  <p className="text-xs text-gray-500 mb-1">Dimension des embeddings</p>
                  <p className="text-lg font-semibold text-gray-900">{vectorAnalysis.embeddingDimension}</p>
                </div>
                <div className="bg-white p-3 rounded-lg border border-gray-200">
                  <p className="text-xs text-gray-500 mb-1">Méthode de similarité</p>
                  <p className="text-lg font-semibold text-gray-900">{vectorAnalysis.similarityMethod || "Cosinus"}</p>
                </div>
              </div>
            ) : (
              <p className="text-center text-gray-600 py-6">Aucune analyse vectorielle disponible</p>
            )}
          </div>
        )}
      </div>

      {/* Documents similaires */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Plus className="h-5 w-5 text-blue-500" />
            <h4 className="text-base font-medium text-gray-800">Documents similaires</h4>
          </div>
          <button 
            onClick={() => setShowSimilar(!showSimilar)}
            className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
          >
            {showSimilar ? 'Masquer les documents' : 'Afficher les documents'}
            <ArrowRight className={`h-4 w-4 transform transition-transform ${showSimilar ? 'rotate-90' : ''}`} />
          </button>
        </div>

        {showSimilar && (
          <div className="bg-gray-50 rounded-lg border border-gray-100 overflow-hidden">
            {loading.similar ? (
              <div className="flex justify-center py-6">
                <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
              </div>
            ) : error ? (
              <div className="bg-red-50 border border-red-200 m-4 p-3 rounded-lg flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-500" />
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            ) : similarDocuments.length > 0 ? (
              <div className="divide-y divide-gray-200">
                {similarDocuments.map((doc, index) => (
                  <div key={index} className="p-4 hover:bg-gray-100 transition-colors">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium text-gray-900">
                          {doc.metadata?.title || doc.fileName || "Document sans titre"}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`px-2 py-0.5 text-xs rounded-full ${getRelevanceColor(doc.score || 0.5)}`}>
                            {Math.round((doc.score || 0.5) * 100)}% similaire
                          </span>
                          {doc.metadata?.documentType && (
                            <span className="text-xs text-gray-500">
                              {doc.metadata.documentType}
                            </span>
                          )}
                        </div>
                        {doc.analysis?.summary && (
                          <p className="text-sm text-gray-600 mt-2 line-clamp-2">
                            {doc.analysis.summary}
                          </p>
                        )}
                      </div>
                      <a
                        href={doc.fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-4 flex items-center gap-1 px-3 py-1 text-sm bg-white border border-gray-200 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
                      >
                        <ExternalLink className="h-4 w-4" />
                        Voir
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-6 text-center text-gray-600">
                Aucun document similaire trouvé
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default OverviewTab; 